import React, { useEffect, useRef, useState } from "react";
import {
  DraftingCompass,
  Settings,
  Handshake,
  Users,
  Factory,
  ClipboardList,
  HardHat,
  Mail,
} from "lucide-react";
import {
  INNOVATION,
  CAPABILITIES,
  RAAC,
  INDUSTRY,
  IMAGES,
} from "../mock";

const ICONS = {
  DraftingCompass,
  Settings,
  Handshake,
  Users,
  Factory,
  ClipboardList,
  HardHat,
};

// Reveal-on-scroll wrapper
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const SectionHeading = ({ lines, dark = false }) => (
  <>
    <div className="accent-bar" />
    <h2
      className="font-bold"
      style={{
        fontSize: "clamp(28px, 3.4vw, 40px)",
        lineHeight: 1.05,
        color: dark ? "#fff" : "#16233a",
      }}
    >
      {Array.isArray(lines)
        ? lines.map((l, i) => (
            <span key={i}>
              {l}
              {i < lines.length - 1 && <br />}
            </span>
          ))
        : lines}
    </h2>
  </>
);

const Innovation = () => (
  <section style={{ background: "#fff" }} className="py-20 lg:py-28">
    <div className="si-container grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <SectionHeading lines={INNOVATION.heading} />
        <div className="mt-6 space-y-5 max-w-xl">
          {INNOVATION.paragraphs.map((p, i) => (
            <p key={i} style={{ color: "#4a5666", fontSize: 17, lineHeight: 1.7 }}>
              {p}
            </p>
          ))}
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="overflow-hidden shadow-xl">
          <img
            src={IMAGES.glassBuilding}
            alt="Modern engineering architecture"
            className="w-full h-[360px] object-cover"
            style={{ filter: "grayscale(20%)" }}
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const Capabilities = () => (
  <section id="capabilities" style={{ background: "#f4f3f1" }} className="py-16 lg:py-20">
    <div className="si-container grid md:grid-cols-3 gap-0">
      {CAPABILITIES.map((c, i) => {
        const Icon = ICONS[c.icon];
        return (
          <Reveal key={c.title} delay={i * 120}>
            <div
              className="capability-card px-8 py-6 h-full"
              style={{
                borderLeft: i === 0 ? "none" : "1px solid #d9d5cf",
              }}
            >
              <div className="flex justify-center mb-6">
                <Icon className="cap-icon" size={54} strokeWidth={1.3} />
              </div>
              <div className="flex justify-center mb-4">
                <div className="accent-bar" style={{ marginBottom: 0 }} />
              </div>
              <h3
                className="text-center font-bold uppercase"
                style={{ color: "#16233a", fontSize: 21, letterSpacing: "0.03em" }}
              >
                {c.title}
              </h3>
              <p
                className="text-center mt-4"
                style={{ color: "#5a6675", fontSize: 15.5, lineHeight: 1.65 }}
              >
                {c.text}
              </p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const RaacBrac = () => (
  <section id="raac" className="grid lg:grid-cols-2" style={{ background: "#fff" }}>
    <div
      className="min-h-[380px] bg-cover bg-center"
      style={{
        backgroundImage: `url(${IMAGES.raacProduct})`,
        filter: "grayscale(10%)",
      }}
    />
    <div className="flex items-center">
      <div className="px-8 lg:px-16 py-16 lg:py-24 max-w-xl">
        <Reveal>
          <div className="accent-bar" />
          <h2
            className="font-bold uppercase"
            style={{ fontSize: "clamp(30px,3.6vw,44px)", color: "#16233a" }}
          >
            {RAAC.heading}
          </h2>
          <div className="mt-6 space-y-5">
            {RAAC.paragraphs.map((p, i) => (
              <p key={i} style={{ color: "#4a5666", fontSize: 17, lineHeight: 1.7 }}>
                {p}
              </p>
            ))}
          </div>
          <a
            href={RAAC.cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-orange mt-8"
          >
            {RAAC.cta.label}
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const Industry = () => (
  <section className="relative py-20 lg:py-28" style={{ background: "#0d1b2e" }}>
    <div
      className="absolute inset-0"
      style={{
        backgroundImage: `url(${IMAGES.industryBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.12,
      }}
    />
    <div className="si-container relative grid lg:grid-cols-2 gap-14 items-center" style={{ zIndex: 2 }}>
      <Reveal>
        <SectionHeading lines={INDUSTRY.heading} dark />
        <div className="mt-6 space-y-5 max-w-xl">
          {INDUSTRY.paragraphs.map((p, i) => (
            <p key={i} style={{ color: "#b7c1cf", fontSize: 17, lineHeight: 1.7 }}>
              {p}
            </p>
          ))}
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-10">
          {INDUSTRY.items.map((it, i) => {
            const Icon = ICONS[it.icon];
            return (
              <div
                key={it.label}
                className="industry-item flex flex-col items-center text-center px-2"
                style={{
                  borderLeft: i === 0 ? "none" : "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <Icon className="ind-icon" size={48} strokeWidth={1.3} />
                <span
                  className="mt-4 font-semibold uppercase text-white"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.12em", fontSize: 15 }}
                >
                  {it.label}
                </span>
              </div>
            );
          })}
        </div>
      </Reveal>
    </div>
  </section>
);

const ContactCTA = () => {
  const EMAIL = "hello@structuralinnovations.co.uk";
  return (
    <section id="contact" style={{ background: "#f4f3f1" }} className="py-20 lg:py-24">
      <div className="si-container">
        <Reveal>
          <div className="accent-bar" />
          <h2 className="font-bold uppercase" style={{ fontSize: "clamp(28px,3.4vw,40px)", color: "#16233a" }}>
            Get In Touch
          </h2>
          <p className="mt-6 max-w-2xl" style={{ color: "#4a5666", fontSize: 17, lineHeight: 1.7 }}>
            For corporate, licensing and partnership enquiries, please get in
            touch and a member of our team will respond promptly.
          </p>
          <a href={`mailto:${EMAIL}`} className="btn-primary mt-8 inline-flex items-center gap-3">
            <Mail size={18} />
            {EMAIL}
          </a>
        </Reveal>
      </div>
    </section>
  );
};

const MidSections = () => (
  <>
    <Innovation />
    <Capabilities />
    <RaacBrac />
    <Industry />
    <ContactCTA />
  </>
);

export default MidSections;
